import type { OutputThresholds, SignalDotState } from '@arcanum-hq/shared-types';

// deal-signal-overhaul Phase 4 Session 1 (2026-05-11).
//
// Classify a numeric metric against the schema's `outputOverrides[*].thresholds`
// band → 4-state visual indicator (`SignalDotState`). Used by the Signal-tab
// `<HeroMetricCell>` (signalDot prop), `<AssumptionCard>` (per Phase 4
// Session 4 modification), and any future surface that wants a band-driven dot.
//
// Threshold-shape contract (verified verbatim against
// `packages/shared-types/src/lib/model.types.ts:outputThresholdsSchema`):
// `{ warn?: { below?: number, above?: number }, error?: { below?: number,
// above?: number }, feedbackText?, feedbackFix? }`. Field names are
// `below`/`above` (NOT `min`/`max`).
//
// Classification:
//   - `value < error.below`  → 'conservative' (hard breach low)
//   - `value > error.above`  → 'aggressive'   (hard breach high)
//   - `value < warn.below || value > warn.above` (within error envelope) → 'differentiated'
//   - else → 'aligned'
//
// Returns `null` when:
//   - `thresholds` is null/undefined (no band authored for this metric — render
//     the cell WITHOUT a dot per the spec's "no false-positive classification"
//     rule)
//   - `value` is non-numeric or non-finite (NaN, Infinity, string error
//     literals from HF) — null indicates "not classifiable", which the
//     consumer renders as no dot
//
// Pure function, no side effects. Tested in __tests__/signal-classification.spec.ts.

export function classifySignal(
  value: unknown,
  thresholds: OutputThresholds | null | undefined,
): SignalDotState | null {
  if (!thresholds) return null;
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;

  // Hard breach (error band) wins over warn band per severity precedence.
  if (thresholds.error?.below != null && value < thresholds.error.below) {
    return 'conservative';
  }
  if (thresholds.error?.above != null && value > thresholds.error.above) {
    return 'aggressive';
  }

  // Inside error envelope but outside warn envelope → differentiated.
  const insideWarnLow =
    thresholds.warn?.below == null || value >= thresholds.warn.below;
  const insideWarnHigh =
    thresholds.warn?.above == null || value <= thresholds.warn.above;
  if (!insideWarnLow || !insideWarnHigh) {
    return 'differentiated';
  }

  return 'aligned';
}
